/**
 * Option selector handling.
 *
 * Each `[data-option="1|2|3"]` block contains a template child carrying
 * `option="name|value|title|image|bg-color"` attributes. For every possible
 * value of option N we clone the template and wire a click handler that:
 *
 *   1. Updates the `selected` map stored on the host `<smootify-product>`.
 *   2. Toggles an `active` class on the clicked entry (and removes it from
 *      siblings).
 *   3. Dispatches a `changeVariant` CustomEvent on the host so the outer
 *      custom-element can refresh variant-bound nodes.
 */

import type { Product, ProductVariant } from "../types.js";
import { findVariantByOptions } from "./variant.js";

// ---------------------------------------------------------------------------
//  Per-host selected state
// ---------------------------------------------------------------------------

/**
 * We keep the current selection keyed by option-name on the host element as
 * a non-enumerable property. This way multiple `<smootify-product>` elements
 * on the same page do not collide.
 */
interface HostWithSelection extends HTMLElement {
  __lmdrSelected?: Record<string, string>;
}

function getSelection(host: HTMLElement): Record<string, string> {
  const h = host as HostWithSelection;
  if (!h.__lmdrSelected) h.__lmdrSelected = {};
  return h.__lmdrSelected;
}

function setSelectionFromVariant(host: HTMLElement, variant: ProductVariant): void {
  const sel = getSelection(host);
  for (const opt of variant.selectedOptions) {
    sel[opt.name] = opt.value;
  }
}

// ---------------------------------------------------------------------------
//  Events
// ---------------------------------------------------------------------------

export interface ChangeVariantDetail {
  variant: ProductVariant | null;
  selected: Record<string, string>;
  product: Product;
}

function dispatchChangeVariant(host: HTMLElement, detail: ChangeVariantDetail): void {
  host.dispatchEvent(
    new CustomEvent<ChangeVariantDetail>("changeVariant", {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

// ---------------------------------------------------------------------------
//  Rendering
// ---------------------------------------------------------------------------

interface OptionValueView {
  name: string;
  value: string;
}

function fillOptionNode(
  clone: Element,
  view: OptionValueView,
  variant: ProductVariant | null,
): void {
  const nodes: Element[] = clone.hasAttribute("option") ? [clone] : [];
  clone.querySelectorAll("[option]").forEach((n) => nodes.push(n));

  const hasVariantImage = !!variant?.image?.url;

  nodes.forEach((child) => {
    const prop = child.getAttribute("option") ?? "";
    switch (prop) {
      case "name":
        child.textContent = view.name;
        break;
      case "value":
        child.textContent = view.value;
        break;
      case "title":
        child.textContent = `${view.name}: ${view.value}`;
        break;
      case "image":
        if (variant?.image && child instanceof HTMLImageElement) {
          child.src = variant.image.url;
          if (variant.image.altText) child.alt = variant.image.altText;
          // Retire la class `.hide` du container image (template Webflow
          // pose `color-image-container hide` par défaut pour que le texte
          // s'affiche si pas d'image — on inverse quand on A une image).
          const container = child.closest<HTMLElement>(
            ".color-image-container, .option-image-container, [option='bg-color']",
          );
          container?.classList.remove("hide");
          container?.style.removeProperty("display");
        } else if (child instanceof HTMLElement) {
          // Pas d'image variante : cache le container image pour laisser place
          // au texte.
          const container = child.closest<HTMLElement>(
            ".color-image-container, .option-image-container, [option='bg-color']",
          );
          if (container) container.style.display = "none";
        }
        break;
      case "bg-color":
        if (child instanceof HTMLElement) {
          child.style.backgroundColor = view.value;
        }
        break;
      default:
        break;
    }
  });

  // Si une image variante a été posée dans ce clone : on cache les textes
  // redondants (title/value/name) pour laisser place à l'image seule.
  // Comportement attendu user : "photos à la place des noms".
  if (hasVariantImage) {
    nodes.forEach((child) => {
      const prop = child.getAttribute("option") ?? "";
      if (prop === "title" || prop === "value" || prop === "name") {
        if (child instanceof HTMLElement) child.style.display = "none";
      }
    });
  }
}

function clearRendered(parent: Element): void {
  parent
    .querySelectorAll(':scope > [data-lmdr-option-rendered="true"]')
    .forEach((n) => n.remove());
}

/**
 * Build the option selectors under `host`.
 *
 * Expects `host` to contain `[data-option="1|2|3"]` blocks. Inside each
 * block the first element child is treated as a template — it is cloned
 * once per option value, hidden itself, and inserted just before itself.
 */
/**
 * Détecte si une option est l'option par défaut Shopify "Title: Default Title"
 * que produit automatiquement Shopify pour les produits sans variant multi-option.
 * Dans ce cas on cache tout le bloc variant-selector (pas d'intérêt à afficher).
 */
function isDefaultOption(option: { name: string; values: string[] } | null | undefined): boolean {
  if (!option) return true;
  if (option.name === "Title" && option.values.length === 1 && option.values[0] === "Default Title") {
    return true;
  }
  return false;
}

/**
 * Identifie le template à cloner parmi les enfants d'un bloc variant-selector.
 * Priorité :
 *   1. Le container (class="flex") qui a des DESCENDANTS `[option]` — on clone le
 *      1er enfant direct de ce container.
 *   2. Sinon, le 1er child direct de `block` qui contient des `[option]`.
 *   3. Sinon, le 1er child direct de `block` (fallback historique).
 */
function findOptionTemplate(block: HTMLElement): HTMLElement | null {
  // Cherche dans les enfants directs ceux qui ont des descendants [option].
  const directChildren = Array.from(block.children) as HTMLElement[];
  for (const child of directChildren) {
    if (child.querySelector("[option]")) {
      // Si c'est un wrapper .flex, on prend son 1er enfant comme template.
      const firstInner = child.firstElementChild as HTMLElement | null;
      if (firstInner && firstInner.querySelector("[option]")) {
        return firstInner;
      }
      return child;
    }
  }
  // Fallback : 1er enfant direct (compat avec anciens templates).
  return directChildren[0] ?? null;
}

export function hydrateOptions(host: HTMLElement, product: Product): void {
  const blocks = host.querySelectorAll<HTMLElement>("[data-option]");
  const selection = getSelection(host);

  blocks.forEach((block) => {
    const raw = block.getAttribute("data-option") ?? "";
    const idx = Number.parseInt(raw, 10);
    if (!Number.isFinite(idx) || idx < 1 || idx > 3) return;

    // Marque le bloc comme "résolu" — lève le masquage anti-FOUC des children
    // placeholder siblings (CSS dans critical-style.ts).
    block.setAttribute("data-lmdr-option-resolved", "1");

    const option = product.options[idx - 1];

    // Cache entièrement le bloc si l'option est absente OU si c'est l'option
    // par défaut Shopify "Title: Default Title" (= produit sans vraies variantes).
    if (isDefaultOption(option)) {
      block.style.display = "none";
      return;
    }
    block.style.removeProperty("display");

    // Update le <label> textuel du bloc avec le nom de l'option (ex "Taille").
    // Le template Webflow a un `<label class="variant-label">Option Label</label>`
    // qui doit refléter `option.name`.
    const label = block.querySelector<HTMLElement>(":scope > label, :scope > .variant-label");
    if (label) setText(label, option!.name);

    const template = findOptionTemplate(block);
    if (!template) return;

    // Si le template est un <label>, on skip : il doit pas être cloné comme option.
    if (template.tagName === "LABEL") return;

    // Supprime les anciennes placeholders visuels (autres enfants visibles
    // qui ressemblent au template mais sont juste là pour l'UI Designer).
    // On garde UNIQUEMENT le template.
    const templateParent = template.parentElement;
    if (templateParent) {
      Array.from(templateParent.children).forEach((sibling) => {
        if (sibling === template) return;
        if (sibling.hasAttribute("data-lmdr-option-rendered")) return;
        if (sibling.hasAttribute("data-lmdr-option-template")) return;
        // Seulement si sibling est du même type que template (même tagName)
        // ET qu'il a été placé là comme placeholder. Heuristique pragmatique :
        // si tagName identique, on le considère comme placeholder.
        if (sibling.tagName === template.tagName) {
          sibling.remove();
        }
      });
    }

    clearRendered(block);
    template.style.display = "none";
    template.setAttribute("data-lmdr-option-template", "true");

    option!.values.forEach((value) => {
      const clone = template.cloneNode(true) as HTMLElement;
      clone.removeAttribute("data-lmdr-option-template");
      clone.style.removeProperty("display");
      clone.setAttribute("data-lmdr-option-rendered", "true");
      clone.setAttribute("data-option-value", value);
      clone.setAttribute("data-option-name", option!.name);

      const view: OptionValueView = { name: option!.name, value };

      const matching = findVariantByOptions(product, {
        ...selection,
        [option!.name]: value,
      });
      fillOptionNode(clone, view, matching);

      if (selection[option!.name] === value) {
        clone.classList.add("active");
      }

      clone.addEventListener("click", (ev) => {
        ev.preventDefault();
        selection[option!.name] = value;

        // Toggle active class dans tout le container du template.
        (templateParent ?? block)
          .querySelectorAll<HTMLElement>('[data-lmdr-option-rendered="true"]')
          .forEach((sibling) => {
            sibling.classList.toggle(
              "active",
              sibling.getAttribute("data-option-value") === value,
            );
          });

        const variant = findVariantByOptions(product, selection);
        dispatchChangeVariant(host, { variant, selected: { ...selection }, product });
      });

      template.parentElement?.insertBefore(clone, template);
    });
  });
}

function setText(el: Element, text: string): void {
  el.textContent = text;
}

/**
 * Set the current selection from a variant and reflect `active` class on
 * already-rendered option nodes. Used after the initial hydrate + on
 * external variant changes.
 */
export function syncSelectionToVariant(
  host: HTMLElement,
  variant: ProductVariant,
): void {
  setSelectionFromVariant(host, variant);

  host
    .querySelectorAll<HTMLElement>('[data-lmdr-option-rendered="true"]')
    .forEach((node) => {
      const name = node.getAttribute("data-option-name") ?? "";
      const value = node.getAttribute("data-option-value") ?? "";
      const sel = getSelection(host);
      node.classList.toggle("active", sel[name] === value);
    });
}

// Re-exported so custom-element code does not need to import the module twice.
export { findVariantByOptions };
